import React, { useEffect, useRef, useState } from 'react'
import classnames from 'classnames'
import Button from '@SiteComponents/Button'
import FormDropdown, { FormDropdownOption } from '@SiteComponents/FormDropdown'
import getTimecode from '@Utils/GetTimecode'
import { getRemoteMediaDuration } from '@Utils/GetRemoteMediaDuration'
import TrainingSample from '@SiteComponents/TrainingSamples/TrainingSample'

import {
  IDraftSpeakerVoice,
  TSpeakerVoiceLanguagesNamesAndCountries,
} from '@SiteModules/SpeakerVoice/types'

import './SpeakerVoiceData.scss'

interface SpeakerVoiceDataProps {
  readonly draftSpeakerVoice: IDraftSpeakerVoice
  readonly disabled?: boolean
  readonly enableDesignGenerator?: boolean
  /** When design description was collected on step 1, hide the generic Description field on step 2. */
  readonly hideStandaloneDescriptionField?: boolean
  readonly isGeneratingSample?: boolean
  readonly generatedSampleError?: string | null
  readonly languagesNamesAndCountries: TSpeakerVoiceLanguagesNamesAndCountries
  onGenerateSample?(textToGenerate: string): void
  onRemoveGeneratedSample?(url: string): void
  handleDraftChange(updatedFields: Partial<IDraftSpeakerVoice>): void
}

type AttributeKey = 'languageName' | 'languageAccent' | 'gender' | 'ageGroup'

interface AttributeRow {
  id: string
  key: AttributeKey | null
  displayValue: string
}

const ALLOWED_SEARCH_ATTRIBUTE_KEYS = ['languageName', 'languageAccent']

const DEFAULT_NO_RESULT_LABEL = 'No options available'
const NO_RESULT_LABELS_BY_ATTRIBUTE_KEY: Record<AttributeKey, string> = {
  languageName: 'No language found',
  languageAccent: 'No accent found',
  gender: DEFAULT_NO_RESULT_LABEL,
  ageGroup: DEFAULT_NO_RESULT_LABEL,
}

const PRIVACY_OPTIONS: ReadonlyArray<FormDropdownOption<boolean>> = [
  {
    value: true,
    label: 'Private',
    description: 'Only you can access this voice.',
  },
  {
    value: false,
    label: 'Public',
    description: 'Anyone in your workspace can access this voice.',
  },
] as const

const ATTRIBUTE_OPTIONS: ReadonlyArray<{ key: AttributeKey; label: string }> = [
  { key: 'languageName', label: 'Language' },
  { key: 'languageAccent', label: 'Accent' },
  { key: 'gender', label: 'Gender' },
  { key: 'ageGroup', label: 'Age' },
]

const ATTRIBUTE_LABEL_BY_KEY = ATTRIBUTE_OPTIONS.reduce<Record<AttributeKey, string>>((acc, option) => {
  acc[option.key] = option.label
  return acc
}, {
  languageName: 'Language',
  languageAccent: 'Accent',
  gender: 'Gender',
  ageGroup: 'Age',
})

const GENDER_OPTIONS = [
  { value: 'neutral', label: 'Neutral' },
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
]

const GENDER_LABEL_BY_VALUE = GENDER_OPTIONS.reduce<Record<string, string>>((acc, option) => {
  acc[option.value] = option.label
  return acc
}, {})
const GENDER_VALUE_BY_LABEL = GENDER_OPTIONS.reduce<Record<string, string>>((acc, option) => {
  acc[option.label] = option.value
  return acc
}, {})

const AGE_GROUP_OPTIONS = [
  { value: 'young', label: 'Young' },
  { value: 'middle_aged', label: 'Middle-aged' },
  { value: 'old', label: 'Old' },
]

const AGE_GROUP_LABEL_BY_VALUE = AGE_GROUP_OPTIONS.reduce<Record<string, string>>((acc, option) => {
  acc[option.value] = option.label
  return acc
}, {})
const AGE_GROUP_VALUE_BY_LABEL = AGE_GROUP_OPTIONS.reduce<Record<string, string>>((acc, option) => {
  acc[option.label] = option.value
  return acc
}, {})

const rowsAreEqual = (currentRows: AttributeRow[], nextRows: AttributeRow[]) => {
  if (currentRows.length !== nextRows.length) { return false }

  for (let index = 0; index < currentRows.length; index += 1) {
    const currentRow = currentRows[index]
    const nextRow = nextRows[index]

    if (currentRow.id !== nextRow.id || currentRow.key !== nextRow.key || currentRow.displayValue !== nextRow.displayValue) {
      return false
    }
  }

  return true
}

const SpeakerVoiceData: React.FC<SpeakerVoiceDataProps> = props => {
  const {
    draftSpeakerVoice,
    disabled,
    languagesNamesAndCountries,
    handleDraftChange
  } = props

  const {
    name,
    private: isPrivate,
    languageName,
    languageAccent,
    ageGroup,
    gender,
    description,
    trainingUrls = [],
  } = draftSpeakerVoice

  const rowIdRef = useRef(0)

  const [languageNameOptions, setLanguageNameOptions] = useState<string[]>([])
  const [languageAccentOptions, setLanguageAccentOptions] = useState<string[]>([])
  const [attributeRows, setAttributeRows] = useState<AttributeRow[]>([])
  const [textToGenerate, setTextToGenerate] = useState('')
  const [generatedSamplesDurationByUrl, setGeneratedSamplesDurationByUrl] = useState<Record<string, number | undefined>>({})

  const canAddAttribute = attributeRows.length < ATTRIBUTE_OPTIONS.length

  useEffect(() => {
    if (!languageAccent) {
      setLanguageNameOptions(Object.keys(languagesNamesAndCountries).sort())
      return
    }

    const relevantLanguageNamesOptions = Object.entries(languagesNamesAndCountries).filter(([_, countries]) =>
      countries.includes(languageAccent)
    ).map(([relevantName]) => relevantName)

    if (languageName && !relevantLanguageNamesOptions.includes(languageName)) {
      handleDraftChange({ languageName: undefined })
    }

    setLanguageNameOptions(relevantLanguageNamesOptions.sort())
  }, [handleDraftChange, languageAccent, languageName, languagesNamesAndCountries])

  useEffect(() => {
    if (!languageName) {
      const allAccents = Array.from(new Set(Object.values(languagesNamesAndCountries).flat())).sort()
      setLanguageAccentOptions(allAccents)
      return
    }

    const relevantLanguageAccentsOptions = languagesNamesAndCountries[languageName] || []

    if (languageAccent && !relevantLanguageAccentsOptions.includes(languageAccent)) {
      handleDraftChange({ languageAccent: undefined })
    }

    setLanguageAccentOptions(relevantLanguageAccentsOptions.sort())
  }, [handleDraftChange, languageAccent, languageName, languagesNamesAndCountries])

  useEffect(() => {
    if (!props.enableDesignGenerator || trainingUrls.length === 0) {
      setGeneratedSamplesDurationByUrl({})
      return
    }

    const urls = Array.from(new Set(trainingUrls))
    let cancelled = false

    const loadDurations = async () => {
      const entries = await Promise.all(
        urls.map(async url => {
          const duration = await getRemoteMediaDuration(url)
          return [url, duration] as const
        })
      )

      if (cancelled) { return }

      setGeneratedSamplesDurationByUrl(prev => {
        const next: Record<string, number | undefined> = {}

        urls.forEach(url => {
          const durationFromRequest = entries.find(([entryUrl]) => entryUrl === url)?.[1]
          next[url] = durationFromRequest ?? prev[url]
        })

        return next
      })
    }

    loadDurations()

    return () => {
      cancelled = true
    }
  }, [props.enableDesignGenerator, trainingUrls])

  useEffect(() => {
    setAttributeRows(prevRows => {
      let nextRows = prevRows.map(row => {
        if (!row.key) { return row }

        const nextValue = (() => {
          switch (row.key) {
          case 'languageName':
            return languageName ?? ''
          case 'gender':
            return gender ?? ''
          case 'ageGroup':
            return ageGroup ?? ''
          case 'languageAccent':
            return languageAccent ?? ''
          default:
            return ''
          }
        })()

        const formattedValue = row.key && nextValue ? formatAttributeValue(row.key, nextValue) : ''

        if (row.displayValue === formattedValue) { return row }

        return {
          ...row,
          displayValue: formattedValue,
        }
      })

      const ensureRow = (key: AttributeKey, value: string | undefined) => {
        if (!value) { return }

        const hasRow = nextRows.some(row => row.key === key)
        if (hasRow) { return }

        nextRows = [...nextRows, createRow(key, value)]
      }

      ensureRow('languageName', languageName)
      ensureRow('languageAccent', languageAccent)
      ensureRow('gender', gender)
      ensureRow('ageGroup', ageGroup)

      return rowsAreEqual(prevRows, nextRows) ? prevRows : nextRows
    })
  }, [languageName, languageAccent, ageGroup, gender])

  const normalizeAttributeValue = (key: AttributeKey, displayValue: string): string => {
    const sanitizedValue = displayValue.trim()
    if (!sanitizedValue) { return '' }

    switch (key) {
    case 'gender':
      return GENDER_VALUE_BY_LABEL[sanitizedValue] ?? sanitizedValue.toLowerCase()
    case 'ageGroup':
      return AGE_GROUP_VALUE_BY_LABEL[sanitizedValue] ?? sanitizedValue.toLowerCase()
    default:
      return sanitizedValue
    }
  }

  const formatAttributeValue = (key: AttributeKey, storedValue: string): string => {
    if (!storedValue) { return '' }

    switch (key) {
    case 'gender':
      return GENDER_LABEL_BY_VALUE[storedValue] ?? storedValue
    case 'ageGroup':
      return AGE_GROUP_LABEL_BY_VALUE[storedValue] ?? storedValue
    default:
      return storedValue
    }
  }

  const createRow = (key: AttributeKey | null, storedValue?: string): AttributeRow => {
    const id = `attribute-${rowIdRef.current}`
    rowIdRef.current += 1

    const displayValue = key ? formatAttributeValue(key, storedValue ?? '') : storedValue ?? ''

    return {
      id,
      key,
      displayValue,
    }
  }

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleDraftChange({ name: event.target.value })
  }

  const handlePrivacyChange = (nextValue: boolean | null) => {
    handleDraftChange({ private: nextValue ?? undefined })
  }

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleDraftChange({ description: event.target.value })
  }

  const handleTextToGenerateChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextToGenerate(event.target.value)
  }

  const handleAttributeKeyChange = (rowId: string, nextKey: AttributeKey | null) => {
    const targetRow = attributeRows.find(row => row.id === rowId)
    if (!targetRow) { return }

    const previousKey = targetRow.key

    if (previousKey === nextKey) { return }

    setAttributeRows(prevRows => prevRows.map(row => {
      if (row.id !== rowId) { return row }

      return {
        ...row,
        key: nextKey,
        displayValue: previousKey && previousKey === nextKey ? row.displayValue : '',
      }
    }))

    if (previousKey) {
      handleDraftChange({ [previousKey]: undefined })
    }

    if (nextKey && previousKey === nextKey && targetRow.displayValue) {
      const normalizedValue = normalizeAttributeValue(nextKey, targetRow.displayValue)
      handleDraftChange({ [nextKey]: normalizedValue })
    }
  }

  const handleAttributeValueChange = (rowId: string, newDisplayValue: string | null) => {
    const targetRow = attributeRows.find(row => row.id === rowId)
    if (!targetRow || !targetRow.key) { return }

    setAttributeRows(prevRows => prevRows.map(row => {
      if (row.id !== rowId) { return row }
      return {
        ...row,
        displayValue: newDisplayValue ?? '',
      }
    }))

    const normalizedValue = normalizeAttributeValue(targetRow.key, newDisplayValue ?? '')
    handleDraftChange({ [targetRow.key]: normalizedValue })
  }

  const addAttribute = () => {
    if (!canAddAttribute) { return }

    setAttributeRows(prevRows => ([
      ...prevRows,
      createRow(null),
    ]))
  }

  const privacyValue = typeof isPrivate === 'boolean' ? isPrivate : null

  const getKeyOptionsForRow = (rowId: string): FormDropdownOption<AttributeKey>[] => {
    const currentRow = attributeRows.find(row => row.id === rowId)
    const unavailableKeys = attributeRows.reduce<Set<AttributeKey>>((acc, row) => {
      if (row.key && row.id !== rowId) {
        acc.add(row.key)
      }
      return acc
    }, new Set())

    return ATTRIBUTE_OPTIONS
      .filter(option => option.key === currentRow?.key || !unavailableKeys.has(option.key))
      .map(option => ({ value: option.key, label: option.label }))
  }

  const getValueOptionsForKey = (key: AttributeKey | null): FormDropdownOption<string>[] => {
    if (!key) { return [] }

    switch (key) {
    case 'gender':
      return GENDER_OPTIONS.map(option => ({ value: option.label, label: option.label }))
    case 'ageGroup':
      return AGE_GROUP_OPTIONS.map(option => ({ value: option.label, label: option.label }))
    case 'languageName':
      return languageNameOptions.map(option => ({ value: option, label: option }))
    case 'languageAccent':
      return languageAccentOptions.map(option => ({ value: option, label: option }))
    }
  }

  const getLabelByKey = (key: AttributeKey) => {
    return ATTRIBUTE_LABEL_BY_KEY[key]
  }

  const identity = (x: any) => {
    return x
  }

  const stopPropagation = (e: React.KeyboardEvent) => {
    e.stopPropagation()
  }

  const handleGenerateSample = () => {
    const trimmedText = textToGenerate.trim()
    if (!trimmedText || !props.onGenerateSample) { return }

    props.onGenerateSample(trimmedText)
  }

  const renderGeneratedSamples = () => {
    if (!props.enableDesignGenerator || trainingUrls.length === 0) { return null }

    return (
      <div className="SpeakerVoiceData__generated-samples">
        {trainingUrls.map(url => {
          const fileName = url.split('/').pop() || 'Generated sample'
          const durationSeconds = generatedSamplesDurationByUrl[url]
          const duration = durationSeconds !== undefined ? getTimecode(durationSeconds, false) : '—'
          const onRemove = () => props.onRemoveGeneratedSample?.(url)

          return (
            <TrainingSample
              key={url}
              fileName={fileName}
              url={url}
              duration={duration}
              onRemove={onRemove}
              allowRemoval
            />
          )
        })}
      </div>
    )
  }

  return (
    <div className={classnames('SpeakerVoiceData', { 'SpeakerVoiceData--disabled': disabled })}>
      <div className="SpeakerVoiceData__row">
        <label className="SpeakerVoiceData__field">
          <span className="SpeakerVoiceData__label">Voice name</span>
          <input
            className="SpeakerVoiceData__input"
            type="text"
            placeholder="Enter the name of your voice"
            value={name ?? ''}
            onChange={handleNameChange}
            disabled={disabled}
          />
        </label>
        <label className="SpeakerVoiceData__field">
          <span className="SpeakerVoiceData__label">Privacy</span>
          <FormDropdown
            value={privacyValue}
            placeholder="Select the privacy"
            options={PRIVACY_OPTIONS}
            disabled={disabled}
            onChange={handlePrivacyChange}
          />
        </label>
      </div>

      {props.enableDesignGenerator && (
        <div className="SpeakerVoiceData__description SpeakerVoiceData__description--voice-studio">
          <span className="SpeakerVoiceData__label">Voice Description</span>
          <textarea
            className="SpeakerVoiceData__input SpeakerVoiceData__input--voice-description"
            placeholder="Describe the voice... e.g., A young energetic woman with a British accent."
            value={description ?? ''}
            onChange={handleDescriptionChange}
            disabled={disabled}
            onKeyDown={stopPropagation}
          />
          <p className="SpeakerVoiceData__voice-description-tip">
            💡 Tip: Include gender, age, accent, and emotion (e.g., calm, excited, authoritative).
          </p>
        </div>
      )}

      <div className="SpeakerVoiceData__attributes">
        {attributeRows.length > 0 && (
          <div className="SpeakerVoiceData__row SpeakerVoiceData__row--headers">
            <span className="SpeakerVoiceData__header">Attributes</span>
            <span className="SpeakerVoiceData__header">Values</span>
          </div>
        )}
        <ul className="SpeakerVoiceData__row SpeakerVoiceData__row--labels">
          {attributeRows.map(row => (
            <li
              className="SpeakerVoiceData__row"
              key={row.id}
            >
              <label className="SpeakerVoiceData__field">
                <FormDropdown
                  value={row.key}
                  placeholder="Select attribute"
                  options={getKeyOptionsForRow(row.id)}
                  disabled={disabled}
                  allowClear
                  clearLabel="Select attribute"
                  onChange={handleAttributeKeyChange.bind(null, row.id)}
                  getValueLabel={getLabelByKey}
                />
              </label>
              <label className="SpeakerVoiceData__field">
                <FormDropdown
                  value={row.displayValue || null}
                  placeholder={row.key ? 'Select the value' : 'Select an attribute first'}
                  options={getValueOptionsForKey(row.key)}
                  disabled={disabled || !row.key}
                  allowClear
                  allowSearch={ALLOWED_SEARCH_ATTRIBUTE_KEYS.includes(row.key ?? '')}
                  clearLabel="Clear value"
                  searchPlaceholder="Search language"
                  noResultsLabel={NO_RESULT_LABELS_BY_ATTRIBUTE_KEY[row.key ?? 'gender']}
                  onChange={handleAttributeValueChange.bind(null, row.id)}
                  getValueLabel={identity}
                />
              </label>
            </li>
          ))}
        </ul>
      </div>

      <div className="SpeakerVoiceData__actions">
        <Button
          styleType="gray"
          size="large"
          caption="Add attribute"
          type="button"
          onClick={addAttribute}
          disabled={!canAddAttribute || disabled}
        />
      </div>

      {props.enableDesignGenerator && (
        <React.Fragment>
          <div className="SpeakerVoiceData__description SpeakerVoiceData__description--text-to-generate">
            <span className="SpeakerVoiceData__label">Text to generate</span>
            <textarea
              className="SpeakerVoiceData__input"
              placeholder="Write the text to say by the new voice."
              value={textToGenerate}
              onChange={handleTextToGenerateChange}
              disabled={disabled}
              onKeyDown={stopPropagation}
            />
          </div>
          <div className="SpeakerVoiceData__actions SpeakerVoiceData__actions--left">
            <Button
              styleType="gray"
              size="large"
              caption="Generate sample"
              type="button"
              onClick={handleGenerateSample}
              disabled={
                disabled ||
                props.isGeneratingSample ||
                textToGenerate.trim().length === 0 ||
                !description?.trim()
              }
              loader={props.isGeneratingSample}
            />
          </div>
        </React.Fragment>
      )}

      {!props.enableDesignGenerator && !props.hideStandaloneDescriptionField && (
        <div className="SpeakerVoiceData__description">
          <span className="SpeakerVoiceData__label">Description</span>
          <textarea
            className="SpeakerVoiceData__input"
            placeholder="Describe the voice style or context..."
            value={description ?? ''}
            onChange={handleDescriptionChange}
            disabled={disabled}
            onKeyDown={stopPropagation}
          />
        </div>
      )}
      {props.enableDesignGenerator && props.generatedSampleError && (
        <div className="SpeakerVoiceData__error">
          {props.generatedSampleError}
        </div>
      )}
      {renderGeneratedSamples()}
    </div>
  )
}

export default SpeakerVoiceData
